import { useMemo, useState } from 'react'
import { EndpointHint, LoadFailure, Skeleton } from '../../../modules/core/index.js'
import { LineChart, type LineSeries } from '../charts/LineChart.js'
import { StackedBars } from '../charts/StackedBars.js'
import { GOLD, INK, MARINE, SERIES_DASH } from '../charts/chartColors.js'
import { useInsightsMarkers, useInsightsMonths } from '../hooks.js'
import { SCORE_BANDS, SCORE_BAND_COLORS, bandsByPeriod, behaviorSeries, behaviorsOf, markerIndexes, scoreBand } from '../insightsData.js'
import { BEHAVIOR_LABELS, MARKER_AREA_LABELS, dayLabel, monthLabel } from '../labels.js'
import type { InsightsMarker, InsightsPortfolio } from '../types.js'
import { FacilityChoice, chosenRowsOf, validSlots, type ChosenSlots } from './FacilityChoice.js'
import { ShowMore } from './ShowMore.js'

const score = (value: number): string => value.toFixed(0)
const MARKERS_SHOWN = 10
const FACILITIES_SHOWN = 12

/**
 * Reliability over time (plan section 6.2): the portfolio's average score as a line, the band between the best and
 * worst facility, up to four chosen facilities as lines of their own (each with its own dash as well as its colour),
 * the months a setting changed as dotted rules with the changes listed under the chart (never who or the values);
 * the score bands by month; and each behavior on its own, the chosen facilities against the portfolio. The months
 * are read with the two tallies these charts draw, so a large client's span stays light.
 */
export function ReliabilityScreen({ portfolio, span, slots, onSlots, showEndpoints = true }: { portfolio: InsightsPortfolio; span: { from: string; to: string }; slots: ChosenSlots; onSlots: (slots: ChosenSlots) => void; showEndpoints?: boolean }) {
  const { periods } = portfolio
  const [showMarkers, setShowMarkers] = useState(true)
  const months = useInsightsMonths(span.from, span.to, null, ['scores', 'behaviors'])
  const markers = useInsightsMarkers(span.from, span.to, showMarkers)
  const valid = validSlots(slots, portfolio.facilities)
  const chosen = chosenRowsOf(valid, portfolio.facilities)
  const trend = useMemo<LineSeries[]>(
    () => [
      { key: 'portfolio', name: 'Portfolio', color: INK, values: portfolio.totals.series.score, dashed: true },
      ...chosen.map(({ row, color, slot }) => ({ key: row.facilityId, name: row.name, color, dash: SERIES_DASH[slot], values: row.series.score })),
    ],
    [portfolio, chosen],
  )
  const band = useMemo(() => scoreBand(portfolio.facilities, periods), [portfolio, periods])
  const placed = useMemo(() => (showMarkers && markers.data ? markerIndexes(markers.data, periods) : []), [showMarkers, markers.data, periods])
  const rollups = useMemo(() => months.data ?? [], [months.data])
  const bands = useMemo(() => bandsByPeriod(rollups, periods), [rollups, periods])
  const behaviors = useMemo(() => behaviorsOf(rollups), [rollups])
  const behaviorCharts = useMemo(
    () =>
      behaviors.map((behavior) => ({
        behavior,
        series: [
          { key: 'portfolio', name: 'Portfolio', color: INK, values: behaviorSeries(rollups, behavior.key, periods), dashed: true },
          ...chosen.map(({ row, color, slot }) => ({ key: row.facilityId, name: row.name, color, dash: SERIES_DASH[slot], values: behaviorSeries(rollups.filter((rollup) => rollup.facilityId === row.facilityId), behavior.key, periods) })),
        ] as LineSeries[],
      })),
    [behaviors, rollups, periods, chosen],
  )
  const ranked = useMemo(() => [...portfolio.facilities].filter((row) => row.figures.score !== null).sort((a, b) => (b.figures.score ?? 0) - (a.figures.score ?? 0)), [portfolio])

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="flex-1 text-sm font-black text-ink">
            Score trend {showEndpoints && <EndpointHint className="ml-2">GET /insights/portfolio · GET /insights/markers</EndpointHint>}
          </h2>
          <label className="flex items-center gap-1.5 text-[11px] text-ink-mid">
            <input type="checkbox" checked={showMarkers} onChange={(event) => setShowMarkers(event.target.checked)} className="h-3.5 w-3.5 accent-marine" />
            Show setting changes
          </label>
        </div>
        <FacilityChoice rows={portfolio.facilities} slots={valid} onChange={onSlots} />
        <Legend series={trend} band markers={showMarkers} />
        <LineChart periods={periods} series={trend} band={{ low: band.low, high: band.high, name: 'Best to worst facility' }} markers={placed.map((entry) => ({ index: entry.index, label: markerLabel(entry.marker, portfolio) }))} domain={[40, 100]} format={score} periodLabel={monthLabel} title={`Average final score by month, ${periods[0] ? monthLabel(periods[0]) : ''} to ${periods[periods.length - 1] ? monthLabel(periods[periods.length - 1]!) : ''}`} />
        {showMarkers && (
          <div className="text-[11px] text-ink-mid">
            {markers.isPending ? (
              <Skeleton lines={1} label="Loading the setting changes" />
            ) : markers.isError ? (
              <LoadFailure size="xs" message="The setting changes could not be loaded." onRetry={() => void markers.refetch()} retrying={markers.isFetching} />
            ) : placed.length === 0 ? (
              'No setting changed in this span.'
            ) : (
              <ShowMore items={placed} shown={MARKERS_SHOWN} noun="changes" render={(entries) => (
                <ul className="flex flex-col gap-0.5">
                  {entries.map(({ marker }, index) => (
                    <li key={`${marker.at}-${index}`} className="flex items-start gap-2">
                      <span aria-hidden="true" className="mt-1 inline-block h-3 w-0 border-l-2 border-dotted" style={{ borderColor: GOLD }} />
                      <span>{markerLabel(marker, portfolio)}</span>
                    </li>
                  ))}
                </ul>
              )} />
            )}
          </div>
        )}
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="flex-1 text-sm font-black text-ink">
              Where the scores sit {showEndpoints && <EndpointHint className="ml-2">GET /insights/months</EndpointHint>}
            </h2>
            <span className="text-[11px] text-ink-mid">Share of workers by score band, every facility</span>
          </div>
          {months.isPending ? (
            <Skeleton lines={4} label="Loading the months" />
          ) : months.isError ? (
            <LoadFailure size="xs" message="The months could not be loaded." onRetry={() => void months.refetch()} retrying={months.isFetching} />
          ) : (
            <>
              <StackedBars periods={periods} layers={SCORE_BANDS.map((name, index) => ({ key: name, name, color: SCORE_BAND_COLORS[index] ?? INK, values: bands[index] ?? [] }))} normalized periodLabel={monthLabel} format={(value) => String(Math.round(value))} title="Share of workers in each score band by month" />
              <div className="flex flex-wrap gap-3 text-[11px] text-ink-mid">
                {SCORE_BANDS.map((name, index) => (
                  <span key={name} className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-sm" style={{ background: SCORE_BAND_COLORS[index] }} />
                    {name}
                  </span>
                ))}
              </div>
            </>
          )}
        </section>

        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="flex-1 text-sm font-black text-ink">Score by facility, {portfolio.totals.latest ? monthLabel(portfolio.totals.latest, 'long') : 'latest month'}</h2>
            <span className="text-[11px] text-ink-mid">Average final score, 0 to 100</span>
          </div>
          {ranked.length === 0 ? (
            <div className="text-[11px] text-ink-mid">No facility has a scored month in this span yet.</div>
          ) : (
            <ShowMore items={ranked} shown={FACILITIES_SHOWN} noun="facilities" render={(rows) => (
              <ul className="flex flex-col gap-1.5">
                {rows.map((row) => {
                  const color = chosen.find((entry) => entry.row.facilityId === row.facilityId)?.color ?? MARINE
                  return (
                    <li key={row.facilityId} className="flex items-center gap-3 text-[11px]">
                      <span className="w-32 truncate text-ink" title={row.name}>
                        {row.name}
                      </span>
                      <span className="relative h-3.5 flex-1 rounded bg-marine-lt/60" role="img" aria-label={`${row.name} ${row.figures.score?.toFixed(1)}`}>
                        <span className="absolute inset-y-0 left-0 rounded" style={{ width: `${Math.max(0, Math.min(100, row.figures.score ?? 0))}%`, background: color }} />
                      </span>
                      <span className="w-10 text-right font-bold text-ink">{row.figures.score?.toFixed(1)}</span>
                    </li>
                  )
                })}
              </ul>
            )} />
          )}
        </section>
      </div>

      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="flex-1 text-sm font-black text-ink">Each behavior on its own</h2>
          <span className="text-[11px] text-ink-mid">Average value 0 to 100 by month · the weight each carries · the chosen facilities against the portfolio</span>
        </div>
        {months.isPending ? (
          <Skeleton lines={3} label="Loading the behaviors" />
        ) : behaviorCharts.length === 0 ? (
          <div className="rounded-lg border border-marine/15 bg-white px-4 py-6 text-center text-xs text-ink-mid">No behavior has counted in a closed month of this span yet.</div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {behaviorCharts.map(({ behavior, series }) => (
              <div key={behavior.key} className="flex min-w-0 flex-col gap-1.5 rounded-lg border border-marine/15 bg-white px-3 py-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-bold text-ink">{BEHAVIOR_LABELS[behavior.key] ?? behavior.key}</span>
                  <span className="text-[10px] text-ink-mid">{behavior.weight === null ? '' : `weight ${behavior.weight}`}</span>
                </div>
                <LineChart periods={periods} series={series} domain={[0, 100]} format={score} periodLabel={monthLabel} title={`${BEHAVIOR_LABELS[behavior.key] ?? behavior.key} by month`} height={160} width={420} />
              </div>
            ))}
          </div>
        )}
        {behaviorCharts.length > 0 && <Legend series={trend} />}
      </section>
    </div>
  )
}

function Legend({ series, band = false, markers = false }: { series: readonly LineSeries[]; band?: boolean; markers?: boolean }) {
  return (
    <div className="flex flex-wrap gap-3 text-[11px] text-ink-mid">
      {series.map((entry) => (
        <span key={entry.key} className="inline-flex items-center gap-1.5">
          <svg aria-hidden="true" width="18" height="4" viewBox="0 0 18 4">
            <line x1="0" y1="2" x2="18" y2="2" stroke={entry.color} strokeWidth="2" strokeDasharray={entry.dashed ? '6 4' : entry.dash} />
          </svg>
          {entry.name}
        </span>
      ))}
      {band && (
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2 w-3.5 rounded-sm" style={{ background: '#D6EAF0' }} />
          Best to worst facility
        </span>
      )}
      {markers && (
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-0 border-l-2 border-dotted" style={{ borderColor: GOLD }} />
          Setting changed
        </span>
      )}
    </div>
  )
}

/** "3 Oct 2026 · North Campus · facility settings: scoreWeights.callOutRate, tierThresholds.goldMinScore". */
function markerLabel(marker: InsightsMarker, portfolio?: InsightsPortfolio): string {
  const where = marker.facilityId === null ? 'every facility' : (portfolio?.facilities.find((row) => row.facilityId === marker.facilityId)?.name ?? marker.subject ?? marker.facilityId)
  const what = marker.area === 'facility_config' ? MARKER_AREA_LABELS[marker.area] : `${MARKER_AREA_LABELS[marker.area] ?? marker.area}${marker.subject ? ` (${marker.subject})` : ''}`
  const fields = marker.fields.length > 0 ? `: ${marker.fields.slice(0, 4).join(', ')}${marker.fields.length > 4 ? ` and ${marker.fields.length - 4} more` : ''}` : marker.action === 'created' ? ' added' : marker.action === 'deleted' ? ' removed' : ''
  return `${dayLabel(marker.at)} · ${where} · ${what}${fields}`
}
